import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap'
import ReactiveButton from 'reactive-button';
import styles from './OfferDetail.module.scss';

interface ModalProps {
    showAcceptOfferModal: boolean,
    handleAcceptOfferModalClose: () => void,
    acceptOffer: () => Promise<boolean>,
    onAcceptDone: () => void,

    showSkipOfferModal: boolean,
    setSkipOfferModal: (val: boolean) => void,
    skipOffer: () => Promise<boolean>
}

type RequestState = 'idle' | 'loading' | 'success' | 'error';

const AcceptOfferModal: React.FC<ModalProps> = ({ showAcceptOfferModal, handleAcceptOfferModalClose, acceptOffer, onAcceptDone, showSkipOfferModal, setSkipOfferModal,
    skipOffer }) => {

    const [confirmModal, setConfirmModal] = useState(false);
    const [acceptRequestState, setAcceptRequestState] = useState<RequestState>('idle');
    const [skipRequestState, setSkipRequestState] = useState<RequestState>('idle');

    const isAcceptLoading = acceptRequestState === 'loading';
    const isSkipLoading = skipRequestState === 'loading';

    useEffect(() => {
        if (!showAcceptOfferModal) {
            setAcceptRequestState('idle');
        }
    }, [showAcceptOfferModal]);

    useEffect(() => {
        if (!showSkipOfferModal) {
            setSkipRequestState('idle');
        }
    }, [showSkipOfferModal]);

    const acceptTheOffer = async () => {
        if (isAcceptLoading) {
            return;
        }

        setAcceptRequestState('loading');
        const wasAccepted = await acceptOffer();

        if (!wasAccepted) {
            setAcceptRequestState('error');
            return;
        }

        setAcceptRequestState('success');
        setConfirmModal(true);
    }

    const done = () => {
        setConfirmModal(false)
        handleAcceptOfferModalClose();
        setAcceptRequestState('idle');
        onAcceptDone();
    }

    const skipTheOffer = async () => {
        if (isSkipLoading) {
            return;
        }

        setSkipRequestState('loading');
        const wasSkipped = await skipOffer();

        if (!wasSkipped) {
            setSkipRequestState('error');
            return;
        }

        setSkipRequestState('success');
        setSkipOfferModal(false);
    }

    return (
        <>
            <Modal centered show={showAcceptOfferModal} keyboard={!isAcceptLoading}>
                <Modal.Body className="text-center">
                When you accept an offer you can chat with the host who will give you the details on how to get in. 
                Accepting an offer but then not going will show in the statistics on your profile so promoters will be less likely to send offers to such users. 
                Are you sure you want to accept the offer now?
                </Modal.Body>

                <Modal.Footer className={styles.modalActionStack}>
                    <ReactiveButton
                        buttonState={acceptRequestState}
                        onClick={() => { void acceptTheOffer(); }}
                        color={'yellow'}
                        idleText={'Yes'}
                        loadingText={'Accepting'}
                        successText={'Accepted'}
                        errorText={'Error'}
                        type={'button'}
                        className={`link btns btnMO ${styles.modalActionButton}`}
                        outline={false}
                        shadow={false}
                        rounded={false}
                        size={'normal'}
                        block={true}
                        messageDuration={2000}
                        disabled={isAcceptLoading}
                        buttonRef={null}
                        width={'100%'}
                        height={'50px'}
                        animation={true}
                    />
                    <button className={`link btns btnMO ${styles.modalActionButton}`}
                        onClick={handleAcceptOfferModalClose}
                        disabled={isAcceptLoading}>
                        No
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal
                centered
                show={confirmModal}
            >
                <Modal.Body>
                    Now you need to chat to the promoter who made the offer you accepted to arrange entry with him/her. You will be taken to the chat now.
                </Modal.Body>
                <Modal.Footer className={styles.modalActionStack}>
                    <button
                        className={`link btns btnMO ${styles.modalActionButton}`}
                        onClick={done}
                        disabled={isAcceptLoading}
                    >
                        Ok
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showSkipOfferModal}
                centered
                keyboard={!isSkipLoading}
            >
                <Modal.Body className="text-center">
                    Are you sure you want to skip this offer?
                </Modal.Body>
                <Modal.Footer className={styles.modalActionRow}>
                    <button
                        className={`link btns btnMO ${styles.modalActionButton}`}
                        onClick={() => setSkipOfferModal(false)}
                        disabled={isSkipLoading}
                    >
                        No
                    </button>
                    <ReactiveButton
                        buttonState={skipRequestState}
                        onClick={() => { void skipTheOffer(); }}
                        color={'yellow'}
                        idleText={'Yes'}
                        loadingText={'Skipping'}
                        successText={'Skipped'}
                        errorText={'Error'}
                        type={'button'}
                        className={`link btns btnMO ${styles.modalActionButton}`}
                        outline={false}
                        shadow={false}
                        rounded={false}
                        size={'normal'}
                        block={false}
                        messageDuration={2000}
                        disabled={isSkipLoading}
                        buttonRef={null}
                        width={'160px'}
                        height={'50px'}
                        animation={true}
                    />
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default AcceptOfferModal
